// ============================================================
// VISION STORE — Fase 9
// Estado global de câmeras, eventos, violações e streams
// ============================================================
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type {
  CameraDevice, VisionEvent, PPEViolation,
  CameraSnapshot, DroneFlightRecord, VisionInsight, StreamState,
} from '@/types/vision'

export interface VisionState {
  // Cameras
  cameras:     CameraDevice[]
  streams:     Map<string, StreamState>

  // Events
  events:      VisionEvent[]
  violations:  PPEViolation[]
  snapshots:   CameraSnapshot[]

  // Drone
  flights:     DroneFlightRecord[]

  // AI insights
  visionInsights: VisionInsight[]

  // UI
  selectedCameraId: string | null
  isLoading:     boolean
  lastRefreshed?: string

  // Actions
  setCameras:    (cameras: CameraDevice[]) => void
  addCamera:     (camera: CameraDevice)   => void
  updateCameraStatus: (id: string, status: CameraDevice['status']) => void
  removeCamera:  (id: string)             => void

  addEvent:      (event: VisionEvent)     => void
  resolveEvent:  (id: string)             => void
  clearEvents:   ()                       => void

  addViolation:  (v: PPEViolation)        => void
  acknowledgeViolation: (id: string)      => void

  addSnapshot:   (s: CameraSnapshot)     => void
  setSnapshots:  (s: CameraSnapshot[])   => void

  setFlights:    (f: DroneFlightRecord[]) => void
  addFlight:     (f: DroneFlightRecord)  => void

  setVisionInsights: (insights: VisionInsight[]) => void

  updateStream:  (cameraId: string, state: Partial<StreamState>) => void

  selectCamera:  (id: string | null)      => void
  setLoading:    (v: boolean)             => void
}

const MAX_EVENTS    = 200
const MAX_SNAPSHOTS = 100

export const useVisionStore = create<VisionState>()(
  subscribeWithSelector((set, get) => ({
    cameras:      [],
    streams:      new Map(),
    events:       [],
    violations:   [],
    snapshots:    [],
    flights:      [],
    visionInsights: [],
    selectedCameraId: null,
    isLoading:    false,

    // ── Cameras ──────────────────────────────────────────────
    setCameras: cameras =>
      set({ cameras, lastRefreshed: new Date().toISOString() }),

    addCamera: camera =>
      set(s => ({ cameras: [camera, ...s.cameras.filter(c => c.id !== camera.id)] })),

    updateCameraStatus: (id, status) =>
      set(s => ({
        cameras: s.cameras.map(c => c.id === id ? { ...c, status } : c),
      })),

    removeCamera: id =>
      set(s => ({ cameras: s.cameras.filter(c => c.id !== id) })),

    // ── Events ───────────────────────────────────────────────
    addEvent: event =>
      set(s => {
        const events = [event, ...s.events.filter(e => e.id !== event.id)]
        return { events: events.slice(0, MAX_EVENTS) }
      }),

    resolveEvent: id =>
      set(s => ({
        events: s.events.map(e => e.id === id ? { ...e, is_resolved: true } : e),
      })),

    clearEvents: () => set({ events: [] }),

    // ── Violations ───────────────────────────────────────────
    addViolation: v =>
      set(s => ({ violations: [v, ...s.violations.filter(x => x.id !== v.id)].slice(0, 100) })),

    acknowledgeViolation: id =>
      set(s => ({
        violations: s.violations.map(v => v.id === id ? { ...v, acknowledged: true } : v),
      })),

    // ── Snapshots ────────────────────────────────────────────
    addSnapshot: snap =>
      set(s => ({
        snapshots: [snap, ...s.snapshots.filter(x => x.id !== snap.id)].slice(0, MAX_SNAPSHOTS),
      })),

    setSnapshots: snapshots =>
      set({ snapshots }),

    // ── Drones ───────────────────────────────────────────────
    setFlights: flights => set({ flights: flights.slice(0, 50) }),
    addFlight:  flight  => set(s => ({ flights: [flight, ...s.flights].slice(0, 50) })), // FIX PROB-08

    // ── Insights ─────────────────────────────────────────────
    setVisionInsights: visionInsights => set({ visionInsights }),

    // ── Streams ──────────────────────────────────────────────
    updateStream: (cameraId, partial) =>
      set(s => {
        const streams = new Map(s.streams)
        const existing: StreamState = streams.get(cameraId) ?? {
          camera_id:     cameraId,
          status:        'online',
          fps_actual:    0,
          latency_ms:    0,
          dropped_frames: 0,
          last_frame_at: new Date().toISOString(),
        }
        streams.set(cameraId, { ...existing, ...partial })
        return { streams }
      }),

    // ── UI ───────────────────────────────────────────────────
    selectCamera: id => set({ selectedCameraId: id }),
    setLoading:   v  => set({ isLoading: v }),
  }))
)

// ─── Derived selectors ────────────────────────────────────────
export const selectOnlineCameras = (s: VisionState) =>
  s.cameras.filter(c => c.status === 'online')

export const selectActiveEvents = (s: VisionState) =>
  s.events.filter(e => !e.is_resolved)

export const selectPendingViolations = (s: VisionState) =>
  s.violations.filter(v => !v.acknowledged)

export const selectCriticalVisionEvents = (s: VisionState) =>
  s.events.filter(e => !e.is_resolved && (e.severity === 'critical' || e.severity === 'emergency'))
